import {
	baseApiHeaders,
	HIDE_TOAST_KEY,
	type IActivityLog,
	type IApiResponse,
	type IFlashMessage,
	type IUser,
	type IUserPreference,
	type IUserReferral,
	type IUserWallet,
	type ParamType,
	setFiatMetaProps,
	type UpdatePreferenceTypes,
} from 'BreetHelpers';
import { setFiatMeta, setMeta, setPreference, setUserData, setWallet } from 'BreetRedux';

import { apiSlice } from './apiSlice';

export const usersApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getUser: builder.query<IApiResponse<IUser>, void>({
			query: () => ({ url: '/users/user' }),
			onQueryStarted: async (_userId, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(setFiatMeta(setFiatMetaProps(data)));
					if (data.meta) dispatch(setMeta(data.meta));
					dispatch(setUserData(data.data));
				} catch (error) {
					console.error(error);
				}
			},
			providesTags: ['User'],
		}),
		updateUser: builder.mutation<IApiResponse<IUserPreference>, ParamType>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/profile',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
		}),
		getUserPreference: builder.query<IApiResponse<IUserPreference>, void>({
			query: () => ({ url: '/users/preference', headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' } }),
			onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(setPreference(data.data));
				} catch (error) {
					console.error(error);
				}
			},
			providesTags: ['GetUserPreference'],
		}),
		updatePreference: builder.mutation<IApiResponse<IUserPreference>, UpdatePreferenceTypes>({
			query: (body) => ({
				method: 'PUT',
				url: '/users/preference',
				body,
				headers: { ...baseApiHeaders, [HIDE_TOAST_KEY]: 'true' },
			}),
			invalidatesTags: ['GetUserPreference', 'GetBeneficiaries'],
		}),
		getActivityLogs: builder.query<IApiResponse<IActivityLog[]>, ParamType>({
			query: (params) => ({ url: '/users/user/activity-logs', params }),
		}),
		getUserReferral: builder.query<IApiResponse<IUserReferral>, void>({
			query: () => ({ url: '/users/referral' }),
		}),
		getFlashMessage: builder.query<IApiResponse<IFlashMessage>, void>({
			query: () => ({ url: '/users/flash' }),
		}),
		getUserWallets: builder.query<IApiResponse<IUserWallet[]>, void>({
			query: () => ({ url: '/users/user/wallet' }),
			onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(setWallet(data.data ?? []));
				} catch (error) {
					console.error(error);
				}
			},
			providesTags: ['GetUserWallets'],
		}),
	}),
});

export const {
	useGetUserQuery,
	useGetActivityLogsQuery,
	useGetUserReferralQuery,
	useGetFlashMessageQuery,
	useGetUserWalletsQuery,
	useUpdatePreferenceMutation,
	useGetUserPreferenceQuery,
	useUpdateUserMutation,
} = usersApiSlice;

export const selectUser = usersApiSlice.endpoints.getUser.select();
export const selectUserPreference = usersApiSlice.endpoints.getUserPreference.select();
